import type { ThemeMode } from "../styles/colorTokens";

const THEME_KEY = "lotscanner-theme";

type ThemeListener = (theme: ThemeMode) => void;

const listeners = new Set<ThemeListener>();
let lastApplied: ThemeMode | null = null;

/**
 * Observe applied theme changes (the `data-theme` flip on `<html>`).
 * Used by the 3D scene to re-theme live alongside the CSS token layer —
 * CSS custom properties flip for free, WebGL materials do not.
 */
export function onThemePreferenceChange(listener: ThemeListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Read persisted theme preference, defaulting to dark (product default). */
export function getStoredThemePreference(): ThemeMode {
  try {
    const value = window.localStorage.getItem(THEME_KEY);
    return value === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
}

/** Apply `data-theme` on `<html>` so token + marketing CSS resolve correctly. */
export function applyThemePreference(theme: ThemeMode): void {
  document.documentElement.dataset.theme = theme;
  // Notify only on actual changes — the provider re-applies on every mount.
  if (theme !== lastApplied) {
    lastApplied = theme;
    for (const listener of listeners) listener(theme);
  }
}

/** Persist and apply a theme preference. */
export function setStoredThemePreference(theme: ThemeMode): void {
  applyThemePreference(theme);
  try {
    window.localStorage.setItem(THEME_KEY, theme);
  } catch {
    // Non-fatal: preference won't survive reload.
  }
}
